import { ReactNode, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Search from '../../../../../components/Search';
import useTanstackVirtualizer from '@hooks/useTanstackVirtualizer';
import { VIRTUALIZER_ESTIMATE_SIZE } from '@constants/constants';
import SectionHeader from './SectionHeader';

const ROW_DIVIDER = '1px solid #2F736E1F';

interface SelectableListProps<T> {
  items: T[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Extract the unique key from an item */
  getItemId: (item: T) => string;
  /** Return true if the item matches the current search string */
  filterItem: (item: T, search: string) => boolean;
  /** Render a single row; receives isSelected so the row can style itself */
  renderRow: (item: T, isSelected: boolean, onToggle: () => void) => ReactNode;
  searchPlaceholder?: string;
  /** Estimated row height for the virtualizer — must match actual row height to avoid clipping */
  itemHeight?: number;
}

// Client-side list — no server pagination, so these are static no-ops for the hook.
const NO_NEXT_PAGE = false;
const NO_FETCH = () => {};

function SelectableList<T>({
  items,
  selectedIds,
  onSelectionChange,
  getItemId,
  filterItem,
  renderRow,
  searchPlaceholder,
  itemHeight = VIRTUALIZER_ESTIMATE_SIZE,
}: SelectableListProps<T>) {
  const [search, setSearch] = useState('');

  const selectedRef = useRef<HTMLDivElement>(null);
  const unselectedRef = useRef<HTMLDivElement>(null);

  const selectedItems = items.filter((item) => selectedIds.includes(getItemId(item)));
  const unselectedItems = items.filter((item) => !selectedIds.includes(getItemId(item)));

  const visibleSelected = search
    ? selectedItems.filter((item) => filterItem(item, search))
    : selectedItems;
  const visibleUnselected = search
    ? unselectedItems.filter((item) => filterItem(item, search))
    : unselectedItems;

  const { rowVirtualizer: selectedVirtualizer } = useTanstackVirtualizer({
    rows: visibleSelected,
    parentRef: selectedRef,
    itemHeight,
    hasNextPage: NO_NEXT_PAGE,
    isFetchingNextPage: false,
    fetchNextPage: NO_FETCH,
  });

  const { rowVirtualizer: unselectedVirtualizer } = useTanstackVirtualizer({
    rows: visibleUnselected,
    parentRef: unselectedRef,
    itemHeight,
    hasNextPage: NO_NEXT_PAGE,
    isFetchingNextPage: false,
    fetchNextPage: NO_FETCH,
  });

  const handleSelectAll = () => onSelectionChange(items.map(getItemId));
  const handleReset = () => onSelectionChange([]);
  const handleToggle = (item: T) => {
    const id = getItemId(item);
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id],
    );
  };

  const hasSelected = visibleSelected.length > 0;
  const hasUnselected = visibleUnselected.length > 0;
  const isAllSelected = items.length > 0 && selectedIds.length === items.length;

  // Suppress bulk-action links while searching (they act on ALL items, not just visible ones)
  const showActions = !search;

  const selectedListHeight = hasUnselected ? 275 : 600;
  const unselectedListHeight = hasSelected ? 275 : 600;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '0.5 rem' }}>
      <Search
        search={search}
        setSearch={setSearch}
        // Local filtering is synchronous; handleSearch is intentionally a no-op
        handleSearch={() => {}}
        placeholder={searchPlaceholder}
        styles={{ backgroundColor: '#2F736E14' }}
      />

      {hasSelected && (
        <Box>
          <SectionHeader
            label="Selected"
            count={selectedIds.length}
            actionLabel={showActions ? 'Reset' : undefined}
            onAction={handleReset}
          />
          <Box ref={selectedRef} sx={{ maxHeight: selectedListHeight, overflowY: 'auto' }}>
            <div
              style={{
                height: `${selectedVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {selectedVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = visibleSelected[virtualRow.index];
                if (!item) return null;
                const isLastRow = virtualRow.index === visibleSelected.length - 1;
                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      borderBottom: isLastRow ? undefined : ROW_DIVIDER,
                      boxSizing: 'border-box',
                    }}
                  >
                    {renderRow(item, true, () => handleToggle(item))}
                  </div>
                );
              })}
            </div>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: '1rem' }}>
        <SectionHeader
          label={hasSelected ? 'Un-selected' : 'Un-selected (All)'}
          count={hasSelected ? unselectedItems.length : undefined}
          actionLabel={showActions && !isAllSelected ? 'Select All' : undefined}
          onAction={handleSelectAll}
        />
        {hasUnselected && (
          <Box ref={unselectedRef} sx={{ maxHeight: unselectedListHeight, overflowY: 'auto' }}>
            <div
              style={{
                height: `${unselectedVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {unselectedVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = visibleUnselected[virtualRow.index];
                if (!item) return null;
                const isLastRow = virtualRow.index === visibleUnselected.length - 1;
                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      borderBottom: isLastRow ? undefined : ROW_DIVIDER,
                      boxSizing: 'border-box',
                    }}
                  >
                    {renderRow(item, false, () => handleToggle(item))}
                  </div>
                );
              })}
            </div>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default SelectableList;
